import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { signInWithRedirect } from 'aws-amplify/auth';
import {
  BookmarkIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  PuzzlePieceIcon,
  FolderOpenIcon,
  EnvelopeIcon,
  TagIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { ReactComponent as BrandIcon } from '../assets/BrandIcon.svg';
import WaitlistModal from '../components/WaitlistModal';
import logo4T from '../assets/4Thought-Technologies.png';

const features = [
  {
    icon: SparklesIcon,
    title: 'Auto Summaries',
    body: 'Get a concise summary of every page you save. Skim your library and rediscover content without re-reading every article from scratch.',
  },
  {
    icon: TagIcon,
    title: 'Auto-tagging',
    body: 'Drop in a URL and BookMarker fetches the page, extracts the key details, and applies relevant tags automatically. No manual filing required.',
  },
  {
    icon: MagnifyingGlassIcon,
    title: 'Smart Search',
    body: 'Find anything in your collection instantly — search across titles, tags, summaries, and topics, not just URLs.',
  },
  {
    icon: FolderOpenIcon,
    title: 'Collections',
    body: 'Group bookmarks into curated collections and share them with anyone via a public link — no account required to view.',
  },
  {
    icon: PuzzlePieceIcon,
    title: 'Browser Extension',
    body: 'Save pages in one click from Chrome, Firefox, or Safari without leaving your current tab. BookMarker processes everything in the background.',
  },
  {
    icon: EnvelopeIcon,
    title: 'Weekly Digest',
    body: 'Get a weekly email summary of everything you saved — a gentle nudge to revisit and act on the content you bookmarked.',
  },
  {
    icon: ArrowsRightLeftIcon,
    title: 'Import & Export',
    body: 'Bring in your existing bookmarks from any browser with a standard HTML import. Export any time — your data is always yours.',
  },
  {
    icon: BookmarkIcon,
    title: 'Tag Intelligence',
    body: 'Visualize your tag cloud, explore co-occurrence graphs, and tidy up redundant tags with one-click consolidation.',
  },
  {
    icon: ChartBarIcon,
    title: 'Analytics',
    body: 'See your saving habits at a glance — activity over time, status breakdown, top categories, and tag frequency all in one dashboard.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Public Profile',
    body: 'Share a public profile page showcasing your curated collections — give others a window into what you\'re reading and exploring.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Save',
    body: 'Paste a URL or click the browser extension. BookMarker immediately queues the page for processing.',
  },
  {
    number: '02',
    title: 'Process',
    body: 'The processing pipeline fetches the page, generates a summary, and applies tags — automatically, in the background.',
  },
  {
    number: '03',
    title: 'Discover',
    body: 'Search, browse by tag or collection, and surface content you saved weeks ago as easily as you saved it.',
  },
];

const trustPoints = [
  'Never lose a link again',
  'Find anything, fast',
  'Works in Chrome, Firefox & Safari',
  'Your data is always yours',
];

// A stylized mock bookmark card shown in the hero
const MockBookmarkCard = () => (
  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-5 text-left max-w-sm w-full">
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 mb-1 truncate">medium.com · just now</p>
        <h4 className="text-sm font-semibold text-gray-900 leading-snug">
          The Unreasonable Effectiveness of Just Showing Up Every Day
        </h4>
      </div>
      <BookmarkIcon className="h-4 w-4 text-primary-500 ml-3 mt-0.5 flex-shrink-0" />
    </div>
    <p className="text-xs text-gray-500 leading-relaxed mb-3">
      A data-driven look at how consistency compounds over time — and why streaks matter less than you think.
    </p>
    <div className="flex flex-wrap gap-1.5">
      {['productivity', 'habits', 'self-improvement'].map(tag => (
        <span
          key={tag}
          className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full"
        >
          {tag}
        </span>
      ))}
      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
        ✓ Auto-tagged
      </span>
    </div>
  </div>
);

const Landing = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="min-h-screen bg-white">

      {/* Nav */}
      <header className="border-b border-gray-100 sticky top-0 bg-white/90 backdrop-blur-sm z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BrandIcon className="h-7 w-7" />
            <span className="text-xl font-bold text-gray-900 brand-wordmark">BookMarker</span>
          </div>
          <div className="flex items-center space-x-4 sm:space-x-6">
            <a
              href="#features"
              className="hidden sm:block text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Features
            </a>
            <a
              href="#how-it-works"
              className="hidden sm:block text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              How it works
            </a>
            <button
              onClick={() => signInWithRedirect()}
              className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="btn-cta text-sm px-4 py-2"
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-24">
          <div className="flex flex-col lg:flex-row items-center gap-12">

            {/* Left: copy */}
            <div className="flex-1 text-center lg:text-left">
              <span className="inline-block bg-secondary-100 text-secondary-700 text-xs font-semibold px-3 py-1 rounded-full mb-6 uppercase tracking-wide">
                Private Beta
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Save smarter.<br />
                <span className="text-primary-600">Find anything.</span>
              </h1>
              <p className="text-xl text-gray-500 mb-10 leading-relaxed max-w-lg">
                BookMarker automatically tags, summarizes, and organizes your bookmarks
                — so you spend less time filing and more time reading.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
                <button
                  onClick={() => setShowModal(true)}
                  className="btn-cta text-base px-8 py-3"
                >
                  Join the Waitlist
                </button>
                <button
                  onClick={() => signInWithRedirect()}
                  className="text-base font-medium text-gray-600 hover:text-primary-600 transition-colors"
                >
                  Already have access? Sign In →
                </button>
              </div>
            </div>

            {/* Right: mock UI */}
            <div className="flex-shrink-0 flex justify-center lg:justify-end w-full lg:w-auto">
              <div className="relative">
                {/* Decorative blur blob behind the card */}
                <div className="absolute -inset-4 bg-primary-100 rounded-3xl blur-2xl opacity-50" />
                <MockBookmarkCard />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-gray-100 bg-gray-50 py-5">
        <div className="max-w-4xl mx-auto px-6">
          <ul className="flex flex-wrap justify-center gap-x-6 sm:gap-x-10 gap-y-2">
            {trustPoints.map(point => (
              <li key={point} className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">How it works</h2>
          <p className="text-gray-500 text-center mb-14 max-w-xl mx-auto">
            Three steps from raw URL to organized, searchable knowledge.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map(({ number, title, body }) => (
              <div key={number} className="relative pl-0 md:pl-0">
                <div className="text-5xl font-black text-secondary-300 mb-3 select-none leading-none">
                  {number}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-500 leading-relaxed text-sm">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-gray-50 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Everything you need, nothing you don't
          </h2>
          <p className="text-gray-500 text-center mb-14 max-w-xl mx-auto">
            A focused set of tools built around one goal: making your saved content easy to find and use.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-primary-200 hover:shadow-sm transition-all">
                <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-primary-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 text-center">
        <div className="max-w-xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Stop losing links. Start finding them.
          </h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Join the waitlist for early access. We onboard new users in small batches
            to make sure every account gets a great first experience.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary text-base px-8 py-3"
          >
            Request Early Access
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <img src={logo4T} alt="4Thought Technologies" className="h-6 opacity-70" />
            <span>© {new Date().getFullYear()} 4Thought Technologies, LLC</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms of Service</Link>
            <button onClick={() => signInWithRedirect()} className="hover:text-gray-600 transition-colors">Sign In</button>
          </div>
        </div>
      </footer>

      <WaitlistModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default Landing;
